import React, { useEffect, useState, useRef } from "react";
import useMainContentDimensions from "../../../../utils/mainContentDimensions";
import { formatDate, addDays } from "../../../../utils/common";
import { useDialog } from "../../../../contexts/DialogContext";

import { Box, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import useWindowDimensions from "../../../../utils/windowDimensions";
import useUser from "../../../../hooks/useUser";
import useAuth from "../../../../hooks/useAuth";
import EditDelete from "../../../../components/EditDelete";
import RenewUser from "../../../users/userlist/components/RenewUser";
import { getTimeStamp } from "../../../../utils/common";
import useMediaQuery from "@mui/material/useMediaQuery";

function Expiring({ data, paging, payload, setPayload, isLoading }) {
  const { hasNextPage } = paging || {};
  const { width: mainContentWidth } = useMainContentDimensions();
  const { openDialog } = useDialog();
  const { height } = useWindowDimensions();

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 5,
  });

  const isTablet = useMediaQuery("(max-width:1024px)");
  const isLaptop = useMediaQuery("(min-width:1025px) and (max-width:1440px)");

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);

  const renewClick = (id) => {
    return openDialog(
      "renew_user",
      "Renew User",
      <RenewUser key={getTimeStamp()} userId={id} />,
      "md",
    );
  };

  const onPageChange = (newPaginationModel) => {
    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: payload.pagination.pageSize,
    };
    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const handleSortModelChange = (newModel) => {
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <EditDelete
            rowClick={(e) => {
              e.stopPropagation();
            }}
            renewClick={() => renewClick(params.id)}
            menus={["renew"]}
          />
        </Box>
      ),
    },
    {
      field: "userName",
      headerName: "User Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Link
            to={"/user/details?id=" + params.id}
            style={{ textDecoration: "none", color: "#3b82f6", fontWeight: 600 }}
          >
            {params.value}
          </Link>
        </Box>
      ),
    },
    {
      field: "fullName",
      headerName: "Customer Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "planName",
      headerName: "Plan",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "rateFullName",
      headerName: "Sub Plan",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "expiration",
      headerName: "Expiry Date",
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              backgroundColor: "rgba(239, 68, 68, 0.08)",
              color: "#ef4444",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {params.value}
          </Box>
        </Box>
      ),
    },
  ];

  const paginationMetaRef = useRef();

  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  return (
    <Box sx={{ width: "100%" }}>
      <DataGrid
        autoHeight
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
        }}
        columns={columns}
        rows={data}
        getRowId={(row) => row["radUserId"]}
        rowCount={paging?.totalNumberOfRecords}
        loading={isLoading}
        rowsPerPageOptions={[5]}
        pagination
        page={paging?.pageNumber}
        pageSize={5}
        paginationMode="server"
        onPaginationModelChange={onPageChange}
        paginationMeta={paginationMeta}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        paginationModel={paginationModel}
        getRowHeight={() => 64}
        slots={{
          noRowsOverlay: () => (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="span"
                  sx={{ fontSize: "24px", opacity: 0.5 }}
                  role="img"
                  aria-label="search"
                >
                  🔍
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                No users found
              </Typography>
            </Box>
          ),
        }}
        sx={{
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #f1f5f9",
            color: "#64748b",
            fontSize: "12px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #f1f5f9",
            fontSize: "14px",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f8fafc",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
        }}
      />
    </Box>
  );
}

export default Expiring;
